import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Languages, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RatingComponent from '../components/RatingComponent';
import Alert from '../components/Alert';
import { FeedbackAPI, SessionStore } from '../services/api';

const ResultsPage = () => {
  const { t } = useTranslation();
  const [translationData, setTranslationData] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [improvedTranslation, setImprovedTranslation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get translation data from sessionStorage
    const storedData = SessionStore.getTranslation();
    if (storedData) {
      setTranslationData(storedData);
    } else {
      // No translation data found, redirect to translate page
      navigate('/');
    }
  }, [navigate]);

  const handleCopyTranslation = async () => {
    if (translationData?.translatedText) {
      try {
        await navigator.clipboard.writeText(translationData.translatedText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const handleSubmitFeedback = async () => {
    if (!rating) {
      setError(t('results.ratingRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // PRIVACY-BY-DESIGN: Only send content data, no user identifiers
      // The backend does NOT store any user identity with feedback
      const feedbackData = {
        sourceText: translationData.sourceText,
        translatedText: translationData.translatedText,
        rating: rating,
        userFeedback: feedback,
        improvedTranslation: improvedTranslation || null,
        timestamp: new Date().toISOString(),
        modelUsed: translationData.modelUsed,
        processingTime: translationData.processingTime,
        translationId: translationData.translationId || null,  // Link feedback to translation
      };

      await FeedbackAPI.submitFeedback(feedbackData);
      
      setIsSubmitted(true);
      SessionStore.markFeedbackSubmitted();
      
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewTranslation = () => {
    // Clear session data and navigate to translate page
    SessionStore.clearTranslation();
    navigate('/translate');
  };

  if (!translationData) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-gray-600">{t('results.loading')}</p>
      </div>
    );
  }

  // Determine source and target languages based on direction
  const isNlToSrn = translationData.direction === 'nl-to-srn';
  const sourceLanguage = isNlToSrn ? t('results.dutch') : t('results.sranantongo');
  const targetLanguage = isNlToSrn ? t('results.sranantongo') : t('results.dutch');

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('results.title')}
        </h1>
        <p className="text-gray-600">
          {t('results.subtitle')}
        </p>
      </div>

      {/* Translation Results */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Source Text */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              {sourceLanguage}
            </h3>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-800 whitespace-pre-wrap">
              {translationData.sourceText}
            </p>
          </div>
        </div>

        {/* Translated Text */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">
                {targetLanguage}
              </h3>
            </div>
            <button
              onClick={handleCopyTranslation}
              className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
              title="Copy translation"
            >
              {copied ? (
                <Check size={16} className="text-green-600" />
              ) : (
                <Copy size={16} />
              )}
              <span>{copied ? t('results.copied') : t('results.copy')}</span>
            </button>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-gray-800 whitespace-pre-wrap">
              {translationData.translatedText}
            </p>
          </div>
        </div>
      </div>

      {/* Translation Info */}
      <div className="card bg-blue-50 border-blue-200">
        <button 
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-between p-0 bg-transparent border-none cursor-pointer"
        >
          <div className="flex items-center space-x-2">
            <Languages className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold text-blue-900">
              {t('results.translationDetails')}
            </h3>
            {translationData.corrections && translationData.corrections.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                {translationData.corrections.length} {t('results.corrections')}
              </span>
            )}
          </div>
          <ChevronDown 
            size={20} 
            className={`text-blue-600 transition-transform duration-200 ${
              showDetails ? 'transform rotate-180' : ''
            }`}
          />
        </button>
        
        {showDetails && (
          <div className="space-y-4 mt-4 pt-4 border-t border-blue-200">
            {/* Basic Info */}
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">{t('results.modelUsed')}:</span>
                <p className="text-blue-700">{translationData.modelUsed}</p>
              </div>
              <div>
                <span className="font-medium text-blue-800">{t('results.processingTime')}:</span>
                <p className="text-blue-700">{translationData.processingTime}ms</p>
              </div>
              <div>
                <span className="font-medium text-blue-800">{t('results.timestamp')}:</span>
                <p className="text-blue-700">
                  {new Date(translationData.timestamp).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Corrections Table */}
            {translationData.corrections && translationData.corrections.length > 0 && (
              <div className="border-t border-blue-200 pt-4">
                <h4 className="font-medium text-blue-900 mb-3">Gemaakte correcties:</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-blue-900">#</th>
                        <th className="px-3 py-2 text-left font-medium text-blue-900">Origineel</th>
                        <th className="px-3 py-2 text-left font-medium text-blue-900">Gecorrigeerd</th>
                        <th className="px-3 py-2 text-left font-medium text-blue-900">Reden/Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-200">
                      {translationData.corrections.map((correction, index) => (
                        <tr key={index} className="hover:bg-blue-100 transition-colors">
                          <td className="px-3 py-2 text-blue-700">{index + 1}</td>
                          <td className="px-3 py-2">
                            <code className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-mono">
                              {correction.original}
                            </code>
                          </td>
                          <td className="px-3 py-2">
                            <code className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-mono">
                              {correction.corrected}
                            </code>
                          </td>
                          <td className="px-3 py-2 text-blue-700 text-xs">
                            {correction.reason || (correction.fix_type && `Type: ${correction.fix_type}`) || '–'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Raw Translation Info */}
            {translationData.originalTranslation && translationData.originalTranslation !== translationData.translatedText && (
              <div className="border-t border-blue-200 pt-4">
                <details className="cursor-pointer">
                  <summary className="font-medium text-blue-900 hover:text-blue-800">
                    📋 Ruwe vertaling (voor debugging)
                  </summary>
                  <div className="bg-gray-100 p-3 rounded mt-2 text-xs text-gray-700 whitespace-pre-wrap font-mono overflow-x-auto">
                    {translationData.originalTranslation}
                  </div>
                </details>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert 
          type="error" 
          message={error} 
          onClose={() => setError(null)}
        />
      )}

      {/* Rating Section */}
      {!isSubmitted && !SessionStore.isFeedbackSubmitted() && (
        <div className="space-y-6">
          <RatingComponent
            rating={rating}
            feedback={feedback}
            improvedTranslation={improvedTranslation}
            onRatingChange={setRating}
            onFeedbackChange={setFeedback}
            onImprovedTranslationChange={setImprovedTranslation}
          />

          <div className="flex justify-center space-x-4">
            <button
              onClick={handleSubmitFeedback}
              disabled={isSubmitting || !rating}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('results.submitting')}</span>
                </>
              ) : (
                <>
                  <Check size={16} />
                  <span>{t('results.submitRating')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {(isSubmitted || SessionStore.isFeedbackSubmitted()) && (
        <Alert 
          type="success" 
          message={t('results.successMessage')}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6">
        <button
          onClick={() => navigate('/')}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowLeft size={16} />
          <span>{t('results.backToHome')}</span>
        </button>

        <button
          onClick={handleNewTranslation}
          className="btn-primary flex items-center space-x-2"
        >
          <Languages size={16} />
          <span>{t('results.newTranslation')}</span>
        </button>
      </div>
    </div>
  );
};

export default ResultsPage;